'use client';
// Request Access page — Panorama Towers resident-only private launch
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import AppLogo from '@/components/ui/AppLogo';
import { CheckCircle, ChevronDown, Lock, Users, Shield } from 'lucide-react';

type ResidentType = 'Owner' | 'Renter' | 'Resident' | '';
type FormState = 'default' | 'submitting' | 'success' | 'error';

interface FormData {
  residentType: ResidentType;
  fullName: string;
  tower: string;
  unit: string;
  email: string;
  phone: string;
  purchaseYear: string;
  invitedBy: string;
  newsletter: boolean;
}

interface FieldError {
  [key: string]: string;
}

function InputField({
  label,
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  error,
  required,
  hint,
}: {
  label: string;
  id: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium mb-1.5" style={{ color: 'rgba(242,244,255,0.80)' }}>
        {label} {required && <span style={{ color: '#FF3DF5' }}>*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3.5 py-2.5 text-[14px] rounded-lg transition-all duration-150 focus:outline-none input-neon"
        style={{
          background: '#0F0D1D',
          border: `1px solid ${error ? 'rgba(192,57,43,0.50)' : 'rgba(255,255,255,0.10)'}`,
          color: '#F2F4FF',
        }}
        onFocus={(e) => {
          if (!error) {
            e.currentTarget.style.borderColor = 'rgba(76,201,255,0.40)';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(76,201,255,0.08)';
          }
        }}
        onBlur={(e) => {
          if (!error) {
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      />
      {error && <p className="mt-1.5 text-[12px]" style={{ color: '#FF6B6B' }}>{error}</p>}
      {hint && !error && <p className="mt-1.5 text-[12px]" style={{ color: 'rgba(167,163,196,0.50)' }}>{hint}</p>}
    </div>
  );
}

function SelectField({
  label,
  id,
  value,
  onChange,
  options,
  error,
  required,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
  error?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium mb-1.5" style={{ color: 'rgba(242,244,255,0.80)' }}>
        {label} {required && <span style={{ color: '#FF3DF5' }}>*</span>}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-2.5 text-[14px] rounded-lg appearance-none transition-all duration-150 focus:outline-none input-neon"
          style={{
            background: '#0F0D1D',
            border: `1px solid ${error ? 'rgba(192,57,43,0.50)' : 'rgba(255,255,255,0.10)'}`,
            color: value ? '#F2F4FF' : '#A7A3C4',
          }}
          onFocus={(e) => {
            if (!error) {
              e.currentTarget.style.borderColor = 'rgba(76,201,255,0.40)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(76,201,255,0.08)';
            }
          }}
          onBlur={(e) => {
            if (!error) {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
              e.currentTarget.style.boxShadow = 'none';
            }
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value} style={{ background: '#0F0D1D', color: '#F2F4FF' }}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#A7A3C4' }} />
      </div>
      {error && <p className="mt-1.5 text-[12px]" style={{ color: '#FF6B6B' }}>{error}</p>}
    </div>
  );
}

export default function RequestAccessPage() {
  const router = useRouter();
  const [formState, setFormState] = useState<FormState>('default');
  const [errors, setErrors] = useState<FieldError>({});
  const [form, setForm] = useState<FormData>({
    residentType: '',
    fullName: '',
    tower: '',
    unit: '',
    email: '',
    phone: '',
    purchaseYear: '',
    invitedBy: '',
    newsletter: true,
  });

  const update = (field: keyof FormData) => (value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const validate = (): boolean => {
    const newErrors: FieldError = {};
    if (!form.residentType) newErrors.residentType = 'Please select your resident type.';
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required.';
    if (!form.tower) newErrors.tower = 'Please select your tower.';
    if (!form.unit.trim()) newErrors.unit = 'Unit number is required.';
    if (!form.email.trim()) newErrors.email = 'Email address is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Please enter a valid email address.';
    if (form.residentType === 'Owner' && !form.purchaseYear) newErrors.purchaseYear = 'Purchase year is required for owners.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setFormState('submitting');
    await new Promise((r) => setTimeout(r, 1500));
    setFormState('success');
    setTimeout(() => router.push('/request-access/success'), 1200);
  };

  if (formState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#07060C' }}>
        <div className="text-center px-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: 'rgba(45,212,160,0.10)', border: '1px solid rgba(45,212,160,0.25)' }}
          >
            <CheckCircle size={28} style={{ color: '#2DD4A0' }} />
          </div>
          <h2 className="font-display font-bold text-[24px] mb-2" style={{ color: '#F2F4FF' }}>You&apos;re on the list.</h2>
          <p className="text-[14px]" style={{ color: '#A7A3C4' }}>Taking you to your founding access confirmation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <div className="max-w-xl mx-auto px-6 py-14">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-5">
              <AppLogo size={40} />
            </div>

            {/* Private launch badge */}
            <div className="flex justify-center mb-5">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{ background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.25)' }}
              >
                <Lock size={11} style={{ color: '#9B7FFF' }} />
                <span className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: '#9B7FFF' }}>
                  Private Resident Launch
                </span>
              </div>
            </div>

            <h1 className="font-display font-bold text-[28px] sm:text-[34px] mb-3 leading-tight" style={{ color: '#F2F4FF' }}>
              Join the founding resident network.
            </h1>
            <p className="text-[15px] leading-relaxed" style={{ color: '#A7A3C4' }}>
              This platform is launching privately for Panorama owners, renters, and residents. Access is rolling out in waves — current residents can request their spot now.
            </p>
          </div>

          {/* Who this is for */}
          <div
            className="rounded-xl p-4 mb-6"
            style={{ background: 'rgba(122,92,255,0.05)', border: '1px solid rgba(122,92,255,0.15)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Users size={13} style={{ color: '#9B7FFF' }} />
              <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: '#9B7FFF' }}>
                This is for Panorama residents
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {['Owners', 'Renters', 'Residents & Occupants'].map((type) => (
                <span
                  key={type}
                  className="text-[12px] px-2.5 py-1 rounded-full font-medium"
                  style={{ background: 'rgba(122,92,255,0.10)', color: '#C4BEFF', border: '1px solid rgba(122,92,255,0.20)' }}
                >
                  {type}
                </span>
              ))}
            </div>
          </div>

          {/* Privacy note */}
          <div
            className="flex items-start gap-3 px-4 py-3.5 rounded-xl mb-8"
            style={{ background: 'rgba(76,201,255,0.05)', border: '1px solid rgba(76,201,255,0.12)' }}
          >
            <Shield size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#4CC9FF' }} />
            <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>
              Your phone and email are <strong style={{ color: '#F2F4FF' }}>hidden by default</strong>. Only you control what other residents can see. This platform is resident-operated and not affiliated with building management.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Resident type */}
            <div>
              <p className="text-[13px] font-medium mb-3" style={{ color: 'rgba(242,244,255,0.80)' }}>
                I am a Panorama <span style={{ color: '#FF3DF5' }}>*</span>
              </p>
              <div className="grid grid-cols-3 gap-3">
                {(['Owner', 'Renter', 'Resident'] as ResidentType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => { update('residentType')(type); setErrors((e) => ({ ...e, residentType: '' })); }}
                    className="py-3 rounded-xl text-[14px] font-medium transition-all duration-150 focus-visible:outline-none"
                    style={
                      form.residentType === type
                        ? {
                            background: 'rgba(76,201,255,0.12)',
                            border: '1px solid rgba(76,201,255,0.35)',
                            color: '#4CC9FF',
                          }
                        : {
                            background: '#0F0D1D',
                            border: '1px solid rgba(255,255,255,0.10)',
                            color: '#A7A3C4',
                          }
                    }
                  >
                    {type}
                  </button>
                ))}
              </div>
              {errors.residentType && (
                <p className="mt-1.5 text-[12px]" style={{ color: '#FF6B6B' }}>{errors.residentType}</p>
              )}
            </div>

            <InputField
              label="Full Name"
              id="fullName"
              value={form.fullName}
              onChange={update('fullName') as (v: string) => void}
              placeholder="Your full name"
              error={errors.fullName}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Tower"
                id="tower"
                value={form.tower}
                onChange={update('tower') as (v: string) => void}
                options={[
                  { label: 'Select tower', value: '' },
                  { label: 'Tower 1 (4525)', value: 'Tower 1' },
                  { label: 'Tower 2 (4575)', value: 'Tower 2' },
                ]}
                error={errors.tower}
                required
              />
              <InputField
                label="Unit Number"
                id="unit"
                value={form.unit}
                onChange={update('unit') as (v: string) => void}
                placeholder="e.g. 1804"
                error={errors.unit}
                required
              />
            </div>

            <InputField
              label="Email Address"
              id="email"
              type="email"
              value={form.email}
              onChange={update('email') as (v: string) => void}
              placeholder="your@email.com"
              error={errors.email}
              required
              hint="Used for verification only. Hidden from other residents by default."
            />

            <InputField
              label="Phone Number"
              id="phone"
              type="tel"
              value={form.phone}
              onChange={update('phone') as (v: string) => void}
              placeholder="Optional"
              hint="Optional. Hidden from other residents by default."
            />

            {/* Owner-only: purchase year */}
            {form.residentType === 'Owner' && (
              <div
                className="p-4 rounded-xl space-y-4"
                style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}
              >
                <p className="text-[12.5px]" style={{ color: 'rgba(167,163,196,0.70)' }}>
                  Owner verification requires your approximate purchase year.
                </p>
                <SelectField
                  label="Year of Purchase"
                  id="purchaseYear"
                  value={form.purchaseYear}
                  onChange={update('purchaseYear') as (v: string) => void}
                  options={[
                    { label: 'Select year', value: '' },
                    ...Array.from({ length: 20 }, (_, i) => {
                      const y = 2026 - i;
                      return { label: String(y), value: String(y) };
                    }),
                  ]}
                  error={errors.purchaseYear}
                  required
                />
              </div>
            )}

            {/* Invited by — optional */}
            <InputField
              label="Invited by a neighbor?"
              id="invitedBy"
              value={form.invitedBy}
              onChange={update('invitedBy') as (v: string) => void}
              placeholder="Their name or unit (optional)"
              hint="If a current resident invited you, add their name here. It helps with verification."
            />

            {/* Newsletter opt-in */}
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => update('newsletter')(!form.newsletter)}
                className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-150 active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(76,201,255,0.40)] hover:border-[rgba(76,201,255,0.35)]"
                style={
                  form.newsletter
                    ? { background: 'rgba(76,201,255,0.20)', border: '1px solid rgba(76,201,255,0.40)' }
                    : { background: '#0F0D1D', border: '1px solid rgba(255,255,255,0.15)' }
                }
              >
                {form.newsletter && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5L4 7L8 3" stroke="#4CC9FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.75)' }}>
                Subscribe to the Panorama community newsletter — building updates, community highlights, and resident news. You can unsubscribe at any time.
              </p>
            </div>

            {/* Submit */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={formState === 'submitting'}
                className="btn-primary w-full text-[15px] py-3.5 justify-center"
              >
                {formState === 'submitting' ? (
                  <span className="flex items-center gap-2 justify-center">
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.20)" strokeWidth="3" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="#F2F4FF" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Request Founding Access'
                )}
              </button>
            </div>

            <p className="text-center text-[12px]" style={{ color: 'rgba(167,163,196,0.40)' }}>
              Already have access?{' '}
              <Link href="/login" className="link-neon transition-colors duration-150" style={{ color: '#4CC9FF' }}>
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
